import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Notification from '@rio-cloud/rio-uikit/Notification';

import { getUserProfile } from '@/configuration/login/loginSlice';
import { useAppSelector } from '@/configuration/setup/hooks';
import InternalErrorState from '@/features/app/InternalErrorState';
import { LoadingIndicator } from '@/features/app/LoadingIndicator';
import { WarningFeedbackRequest, WarningFeedbackResponse } from '@/generated/GeneratedApiTypes';
import {
    useCreateWarningFeedbackMutation,
    useDeleteWarningFeedbackMutation,
    useGetWarningFeedbackQuery,
    useUpdateWarningFeedbackMutation,
} from '@/services/warningsApi';
import FeedbackForm from './FeedbackForm';
import { EMPTY_FEEDBACK_FORM_VALUES, type FeedbackFormValues } from './utils/types';

const toFormValues = (response: WarningFeedbackResponse | undefined): FeedbackFormValues => {
    if (!response) {
        return EMPTY_FEEDBACK_FORM_VALUES;
    }
    return {
        serviceActionRequired: response.service_action_required,
        workshopFeedback: response.workshop_feedback as FeedbackFormValues['workshopFeedback'],
        workshopFeedbackOthers: response.workshop_feedback_others ?? '',
        customerFeedback: response.customer_feedback as FeedbackFormValues['customerFeedback'],
        customerFeedbackOthers: response.customer_feedback_others ?? '',
        comments: response.comments ?? '',
    };
};

const toRequestBody = (values: FeedbackFormValues): WarningFeedbackRequest => ({
    service_action_required: values.serviceActionRequired as boolean,
    workshop_feedback: values.workshopFeedback,
    workshop_feedback_others: values.workshopFeedback === 'OTHERS' ? values.workshopFeedbackOthers : undefined,
    customer_feedback: values.customerFeedback,
    customer_feedback_others: values.customerFeedback === 'OTHERS' ? values.customerFeedbackOthers : undefined,
    comments: values.comments === '' ? undefined : values.comments,
});

const FeedbackPage = () => {
    const { warningId } = useParams<{ warningId: string }>();
    const userProfile = useAppSelector(getUserProfile);
    const intl = useIntl();

    // `currentData` (unlike `data`) is not carried over from the previous warningId while a new
    // request is in flight or has failed - it only reflects the result for the current args, so
    // navigating between warnings doesn't keep showing the previous one's stale feedback.
    const {
        currentData: data,
        isLoading,
        isError,
        error,
        refetch,
    } = useGetWarningFeedbackQuery({ warningId: warningId ?? '' }, { skip: !warningId });
    const [createWarningFeedback, { isLoading: isCreating }] = useCreateWarningFeedbackMutation();
    const [updateWarningFeedback, { isLoading: isUpdating }] = useUpdateWarningFeedbackMutation();
    const [deleteWarningFeedback] = useDeleteWarningFeedbackMutation();

    // A 404 means no feedback has been submitted yet for this warning - render an empty form,
    // not an error state.
    const isNotFound = isError && 'status' in error && error.status === 404;

    const [formValues, setFormValues] = useState<FeedbackFormValues>(EMPTY_FEEDBACK_FORM_VALUES);
    const [feedbackExists, setFeedbackExists] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    // Only sync the form from the query's initial load for this warningId (gated on `isLoading`
    // rather than `data`). A save/delete mutation invalidates the query cache, which triggers a
    // background refetch; if that effect depended on `data` directly, the refetch resolving after
    // the user started a new edit would silently overwrite their in-progress changes. Save and
    // delete instead apply their own result directly to the form (see handleSave/handleConfirmDelete).
    const lastSyncedWarningId = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (isLoading || lastSyncedWarningId.current === warningId) {
            return;
        }
        lastSyncedWarningId.current = warningId;
        setFormValues(toFormValues(data));
        setFeedbackExists(Boolean(data));
    }, [warningId, isLoading, data]);

    // This page is opened as a standalone deep link (e.g. from the ServiceCare KPI dashboard),
    // not embedded with a parent to navigate back to. Cancel discards unsaved edits by resetting
    // the form to the last loaded/saved state instead of navigating anywhere.
    const handleCancel = () => {
        setFormValues(toFormValues(data));
    };

    const handleSave = async () => {
        if (!warningId) {
            return;
        }
        const body = toRequestBody(formValues);
        try {
            const response = feedbackExists
                ? await updateWarningFeedback({ warningId, body }).unwrap()
                : await createWarningFeedback({ warningId, body }).unwrap();
            // Apply the mutation's own response directly, rather than relying on the invalidated
            // query's background refetch, so a save can never be clobbered by a late-resolving
            // refetch overwriting state the user has since changed.
            setFormValues(toFormValues(response));
            setFeedbackExists(true);
            Notification.success(intl.formatMessage({ id: 'intl-msg:vehicle-warnings.feedback.saveSuccess' }));
        } catch {
            Notification.error(intl.formatMessage({ id: 'intl-msg:vehicle-warnings.feedback.saveError' }));
        }
    };

    const handleConfirmDelete = async () => {
        if (!warningId) {
            return;
        }
        try {
            await deleteWarningFeedback({ warningId }).unwrap();
            setFormValues(EMPTY_FEEDBACK_FORM_VALUES);
            setFeedbackExists(false);
            Notification.success(intl.formatMessage({ id: 'intl-msg:vehicle-warnings.feedback.deleteSuccess' }));
        } catch {
            Notification.error(intl.formatMessage({ id: 'intl-msg:vehicle-warnings.feedback.deleteError' }));
        } finally {
            setShowDeleteConfirmation(false);
        }
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    if (isError && !isNotFound) {
        return <InternalErrorState reloadTriggerFunction={refetch} />;
    }

    return (
        <div className="display-flex flex-column padding-20">
            <div className="display-flex flex-column gap-15">
                <h1 className="margin-0">
                    <FormattedMessage id="intl-msg:vehicle-warnings.feedback.title" />
                </h1>
                {userProfile?.email && (
                    <span className="text-color-gray text-italic">
                        <FormattedMessage
                            id="intl-msg:vehicle-warnings.feedback.loggedInAs"
                            values={{ email: userProfile.email }}
                        />
                    </span>
                )}
                <span className="text-italic">
                    <FormattedMessage
                        id="intl-msg:vehicle-warnings.feedback.warningId"
                        values={{ warningId: warningId ?? '' }}
                    />
                </span>
                <hr className="margin-y-0" />
                <FeedbackForm
                    values={formValues}
                    onChange={setFormValues}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onDelete={() => setShowDeleteConfirmation(true)}
                    canDelete={feedbackExists}
                    isSaving={isCreating || isUpdating}
                />
            </div>
            <ConfirmationDialog
                show={showDeleteConfirmation}
                title={intl.formatMessage({ id: 'intl-msg:vehicle-warnings.feedback.deleteConfirmTitle' })}
                content={intl.formatMessage({ id: 'intl-msg:vehicle-warnings.feedback.deleteConfirmContent' })}
                bsSize={Dialog.SIZE_SM}
                onClickConfirm={handleConfirmDelete}
                onClickCancel={() => setShowDeleteConfirmation(false)}
                cancelButtonText={intl.formatMessage({ id: 'intl-msg:vehicle-warnings.feedback.deleteConfirmCancel' })}
                confirmButtonText={intl.formatMessage({ id: 'intl-msg:vehicle-warnings.feedback.delete' })}
            />
        </div>
    );
};

export default FeedbackPage;
